import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Instagram,
  Facebook,
  Calendar,
  MapPin,
  Sparkles,
  Radio,
  Mic2,
  Guitar,
  Music,
  Drum,
  ArrowUpRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { ASSETS, TEXTURES, MESSAGES_LINKS } from "@/lib/assets";

const GENRES = ["Alternative Metal"];

const LINEUP = [
  { name: "Ashley Bullock", role: "Lead Vocals", icon: Mic2 },
  { name: "Chris Bullock", role: "Lead Guitar / Programming", icon: Guitar },
  { name: "Andrew Allaband", role: "Rhythm Guitar", icon: Guitar },
  { name: "Reigh Harrison VI", role: "Bass", icon: Music },
  { name: "Nick Mingione", role: "Drums", icon: Drum },
];

const BIO = [
  `For Southern California alternative metal outfit MESSAGES, nearly a decade of reinvention culminated in a powerful 2024 debut, anchored by the breakout success of their single, "Ashes."`,
  `Conceived by lead guitarist and composer Chris Bullock, the band's vision reaches its most complete and potent form with the addition of lead vocalist Ashley Bullock and drummer Nick Mingione, joining rhythm guitarist Andrew Allaband and bassist Reigh Harrison VI.`,
  `Their upcoming single, "Come Alive", is more than a new track — it is a clear declaration of intent — the first chapter of what will be their debut album (Spring 2027). Blending crushing guitar work, intricate rhythms, and striking vocal melodies, the refined five-piece delivers a unified sound that reclaims their story and sets the stage for a new era.`,
];

const SOCIAL_LINKS = [
  {
    label: "Instagram",
    href: MESSAGES_LINKS.instagram,
    icon: Instagram,
    testid: "artist-instagram",
    className:
      "border border-slate/40 text-softwhite hover:border-crimson-bright hover:text-crimson-bright",
  },
  {
    label: "Facebook",
    href: MESSAGES_LINKS.facebook,
    icon: Facebook,
    testid: "artist-facebook",
    className:
      "border border-slate/40 text-softwhite hover:border-crimson-bright hover:text-crimson-bright",
  },
  {
    label: "Listen on Spotify",
    href: MESSAGES_LINKS.spotify,
    icon: null,
    testid: "artist-spotify",
    className: "bg-[#1DB954] hover:bg-[#1ed760] text-black",
    fa: "fa-brands fa-spotify",
  },
];

export default function ArtistMessages() {
  return (
    <div data-testid="artist-messages-page" className="bg-charcoal">
      {/* Back nav */}
      <div className="pt-28 md:pt-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16">
          <Link
            to="/roster"
            data-testid="artist-back-link"
            className="group inline-flex items-center gap-2 font-label uppercase tracking-[0.2em] text-xs text-slate hover:text-softwhite transition-colors"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Back to Roster
          </Link>
        </div>
      </div>

      {/* ------------------ HERO with PRESS PHOTO ------------------ */}
      <section className="relative overflow-hidden pt-10 md:pt-14 pb-20 md:pb-28">
        <img src={TEXTURES.gothic} alt="" className="absolute inset-0 h-full w-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-charcoal/80" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-10 items-center">
          {/* Press photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 border border-slate/20 bg-black flex items-center justify-center overflow-hidden"
          >
            <img
              src={ASSETS.messagesPress}
              alt="MESSAGES — press photo"
              data-testid="press-photo"
              className="w-full h-auto max-h-[720px] object-contain"
            />
          </motion.div>

          {/* Meta */}
          <Reveal delay={0.15} className="lg:col-span-7">
            <span
              data-testid="hero-badge"
              className="inline-block px-3 py-1.5 bg-crimson/20 border border-crimson/50 font-label uppercase tracking-[0.2em] text-[11px] text-crimson-bright"
            >
              New Single — "Come Alive" · Sept 28
            </span>
            <h1 className="mt-6 font-display font-black text-softwhite text-6xl md:text-7xl lg:text-[9rem] leading-[0.85] tracking-tight">
              MESSAGES
            </h1>
            <div className="mt-6 flex flex-wrap gap-2">
              {GENRES.map((g) => (
                <span
                  key={g}
                  data-testid={`genre-${g.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-label uppercase tracking-[0.12em] text-[11px] text-slate border border-slate/40 px-3 py-1"
                >
                  {g}
                </span>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-2 text-slate font-label uppercase tracking-[0.15em] text-xs">
              <MapPin size={14} className="text-crimson-bright" />
              <span data-testid="artist-origin">Southern California, USA</span>
            </div>
            <p className="mt-8 font-display text-softwhite text-2xl md:text-3xl leading-tight max-w-xl">
              "Not You...Not I...WE. ARE. MESSAGES."
            </p>
          </Reveal>
        </div>
      </section>

      {/* ------------------ OFFICIAL LINEUP ------------------ */}
      <section className="relative z-10 pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16">
          <Reveal>
            <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-4">Official Lineup</p>
            <h2 className="font-display text-3xl md:text-5xl text-softwhite">The Five-Piece</h2>
          </Reveal>

          <div
            data-testid="lineup-grid"
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-slate/15 border border-slate/15"
          >
            {LINEUP.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.name} delay={i * 0.08} className="bg-charcoal">
                  <div className="p-6 md:p-7 h-full group hover:bg-surface transition-colors duration-500">
                    <Icon size={22} className="text-crimson-bright" />
                    <p className="mt-6 font-display text-xl md:text-2xl text-softwhite leading-tight">{m.name}</p>
                    <p className="mt-3 font-label uppercase tracking-[0.12em] text-[11px] text-slate">{m.role}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ------------------ BIOGRAPHY ------------------ */}
      <section className="relative z-10 pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4">
            <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-4">Biography</p>
            <h2 className="font-display text-3xl md:text-5xl text-softwhite leading-[1.05] max-w-sm">
              A decade of reinvention. One unified sound.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <div className="border-l-2 border-crimson pl-6 md:pl-10 space-y-6 max-w-3xl" data-testid="artist-bio">
              {BIO.map((p, i) => (
                <p key={i} className="text-softwhite/90 font-body leading-relaxed text-base md:text-lg">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ------------------ FEATURED SINGLE — COME ALIVE ------------------ */}
      <section className="relative z-10 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16">
          <Reveal>
            <div
              data-testid="come-alive-card"
              className="relative bg-surface border border-crimson/40 crimson-glow p-8 md:p-12 overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 h-72 w-72 bg-crimson/15 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 h-72 w-72 bg-crimson/10 blur-3xl pointer-events-none" />

              <div className="relative grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-8">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-crimson/20 border border-crimson/50 font-label uppercase tracking-[0.2em] text-[11px] text-crimson-bright">
                    <Sparkles size={13} /> Featured Single
                  </span>
                  <h2 className="mt-6 font-display text-5xl md:text-7xl text-softwhite leading-[0.9]">
                    Come Alive
                  </h2>
                  <p
                    data-testid="come-alive-subheader"
                    className="mt-5 font-label uppercase tracking-[0.15em] text-xs md:text-sm text-slate"
                  >
                    Radio Premiere — Featured on The Path Radio
                    <span className="text-crimson-bright mx-2">|</span>
                    Global Release: September 28
                  </p>
                  <p className="mt-6 text-softwhite/90 font-body leading-relaxed max-w-2xl">
                    A clear declaration of intent — the first chapter of what will be MESSAGES' debut album (Spring 2027).
                  </p>

                  <div className="mt-10 flex flex-wrap gap-4">
                    <a
                      href={MESSAGES_LINKS.pathRadio}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="listen-path-radio-btn"
                      className="group inline-flex items-center gap-3 bg-crimson hover:bg-crimson-bright text-softwhite px-7 py-4 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
                    >
                      <Radio size={17} /> Listen on The Path Radio
                      <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </a>
                    <a
                      href={MESSAGES_LINKS.presave}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="presave-btn"
                      className="group inline-flex items-center gap-3 border border-softwhite/70 hover:border-softwhite text-softwhite hover:bg-softwhite hover:text-charcoal px-7 py-4 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
                    >
                      Pre-Save "Come Alive"
                      <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </a>
                  </div>
                </div>

                <div className="lg:col-span-4 flex justify-center lg:justify-end">
                  <div className="relative">
                    <div className="absolute inset-0 bg-crimson/30 blur-3xl" />
                    <div className="relative border border-slate/30 bg-charcoal p-8 flex flex-col items-center">
                      <Calendar size={26} className="text-crimson-bright" />
                      <p className="mt-4 font-label uppercase tracking-[0.25em] text-[11px] text-slate">Global Release</p>
                      <p className="mt-2 font-display text-4xl text-softwhite">Sept 28</p>
                      <p className="mt-1 font-label uppercase tracking-[0.2em] text-[10px] text-crimson-bright">2026</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ------------------ SOCIAL & STREAMING ------------------ */}
      <section className="relative z-10 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16">
          <Reveal>
            <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-4">Follow &amp; Listen</p>
            <h2 className="font-display text-3xl md:text-5xl text-softwhite">Stay Connected</h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div
              data-testid="artist-links-container"
              className="mt-10 border border-slate/20 bg-surface p-6 md:p-8 flex flex-wrap items-center gap-4"
            >
              {SOCIAL_LINKS.map((l) => {
                const Icon = l.icon;
                return (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={l.testid}
                    className={`inline-flex items-center gap-2.5 px-6 py-3.5 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300 ${l.className}`}
                  >
                    {l.fa ? <i className={`${l.fa} text-base`} aria-hidden="true" /> : Icon ? <Icon size={17} /> : null}
                    {l.label}
                  </a>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
